import React, { useEffect } from 'react';
import { useHistory } from "react-router-dom";
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import Snackbar from '@material-ui/core/Snackbar';

import MaterialTable from 'material-table'

import AddUserDialog from './AddUserDialog';


const tableRef = React.createRef();

const useStyles = makeStyles(theme => ({
    paper: {
        padding: theme.spacing(3),
    },
    avatar: {
        '& img': {
            maxWidth: '90%',
            width: 75,
        }
    }
}));

const MaterialTableTable = () => {
    const classes = useStyles();

    const provinces = window.provinces;
    const [state, setState] = React.useState({
        columns: [
            {
                title: 'Picture',
                render: rowData => (
                    <div className={classes.avatar}>
                        {rowData.profile_picture_path ? <img src={rowData.profile_picture_path} /> : ''}
                    </div>
                ),
                cellStyle: {
                    width: 78,
                    textAlign: 'center'
                },
                filtering: false
            },
            {
                title: 'Last Name',
                field: 'last_name',
                filtering: false
            },
            {
                title: 'First Name',
                field: 'first_name',
                filtering: false
            },
            {
                title: 'Email',
                field: 'email',
                filtering: false
            },
            {
                title: 'Province',
                render: rowData => {
                    const province = provinces.filter(i => i.id == rowData.province_id)[0];
                    return province ? (
                        <div><span>{province.province_name}</span></div>
                    ) : '';
                },
                filtering: false
            },
            {
                title: 'Officiant',
                filtering: true,
                field: 'is_officiant',
                lookup: {
                    'yes': 'Yes',
                    'no': 'No',
                },
                render: rowData => (
                    <div>
                        {rowData.is_officiant ? 'Yes' : 'No'}
                    </div>
                )
            },
            {
                title: 'Add Couple',
                filtering: true,
                field: 'can_add_couple',
                lookup: {
                    'yes': 'Yes',
                    'no': 'No',
                },
                render: rowData => (
                    <div>
                        {rowData.can_add_couple ? 'Yes' : 'No'}
                    </div>
                )
            },
            {
                title: 'Status',
                filtering: true,
                field: 'is_active',
                lookup: {
                    'yes': 'Active',
                    'no': 'Inactive',
                },
                render: rowData => (
                    <div>
                        {rowData.is_active ? 'Active' : 'Inactive'}
                    </div>
                )
            },
            {
                title: '# Couples',
                field: 'weddings_count',
                filtering: false
            },
        ],
    });

    const pageSizeOptions = [30, 50, 100, 250];

    const fetchData = query =>
        new Promise((resolve, reject) => {
            let url = '/api/admin/users?'

            /* TODO: use orderBy and filters */
            url += 'per_page=' + query.pageSize
            url += '&page=' + (query.page + 1)
            url += '&search=' + query.search
            url += '&orderBy=' + (query.orderBy ? query.orderBy.field : '')
            url += '&orderDirection=' + query.orderDirection
            if (query.filters.length) {
                url += '&' + query.filters.map(x => `${x.column.field}=`+encodeURI(x.value)).join('&')
            }
            fetch(url)
                .then(response => {
                    return response.json();
                })
                .then(result => {
                    tableRef.current.props.options.pageSizeOptions = pageSizeOptions;
                    resolve({
                        data: result.data,
                        page: result.current_page - 1,
                        totalCount: result.total,
                    })
                })
            }
        )

    let history = useHistory();

    function handleEditUser(event, rowData) {
        history.push(`/admin/users/${rowData.id}`);
    }

    return (
        <React.Fragment>
            <MaterialTable
                title="Users List"
                columns={state.columns}
                data={fetchData}
                actions={[
                    {
                        icon: 'edit',
                        tooltip: 'Edit User',
                        onClick: handleEditUser
                    },
                    {
                        icon: 'refresh',
                        tooltip: 'Refresh Data',
                        isFreeAction: true,
                        onClick: () => tableRef.current && tableRef.current.onQueryChange(),
                    }
                ]}
                components={{
                    Container: Box,
                }}
                options={{
                    showTitle: false,
                    pageSize: 30,
                    pageSizeOptions: pageSizeOptions,
                    exportButton: true,
                    loadingType: 'linear',
                    emptyRowsWhenPaging: false,
                    sorting: false,
                    debounceInterval: 600,
                    padding: 'dense',
                    filtering: true
                }}
                tableRef={tableRef}
            />
        </React.Fragment>
    );
};

const UsersList = () => {
    const classes = useStyles();

    useEffect(() => {
        document.title = 'Users - Enduring Promises';
    });

    const [openSnackbar, setOpenSnackbar] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState('');
    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenSnackbar(false);
    };

    const [userDialogOpen, setUserDialogOpen] = React.useState(false);
    const openUserDialog = () => {
        setUserDialogOpen(true);
    };
    const closeUserAddDialog = () => {
        setUserDialogOpen(false);
    };

    const handleUserAdded = () => {
        setSnackbarMessage('User was added!');
        setOpenSnackbar(true);
        tableRef.current && tableRef.current.onQueryChange();
    }

    return (
        <Paper square className={classes.paper}>
            <div style={{ maxWidth: '100%' }}>
                <Typography variant="h6">Users</Typography>
                <Grid
                    container
                    spacing={2}
                    direction="row"
                    justify="space-between"
                    alignItems="center"
                >
                    <Grid item xs={12} sm md style={{textAlign: 'right', minWidth: 142}}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={openUserDialog}
                        >
                            Add User
                        </Button>
                    </Grid>
                </Grid>
                <MaterialTableTable />
                <AddUserDialog open={userDialogOpen} handleClose={closeUserAddDialog} handleUserAdded={handleUserAdded} scroll='paper' />
                <Snackbar
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    open={openSnackbar}
                    autoHideDuration={4000}
                    onClose={handleCloseSnackbar}
                    message={snackbarMessage}
                    action={
                        <React.Fragment>
                            <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseSnackbar}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </React.Fragment>
                    }
                />
            </div>
        </Paper>
    )
}

export default UsersList;
